import "react-day-picker/dist/style.css";
import "./search-date-picker.scss";
import "design-system/components/search/archive-page-facets/archive-page-facets.scss";

import {
  DateFormatter,
  DateRange,
  DayPicker,
  SelectRangeEventHandler,
} from "react-day-picker";
import { format, parse, isValid } from "date-fns";
import { useState, useEffect, ChangeEvent, KeyboardEvent } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import * as Popover from "@radix-ui/react-popover";
import { useBreakpoint } from "design-system/hooks/use-breakpoint";
import { Icon } from "design-system/components/icons/icon";

const stringToDate = (value: string) => {
  const date = parse(value, "M/d/y", new Date());
  return isValid(date) ? date : undefined;
};

interface SearchDatePickerProps {
  selectedRange: DateRange;
  handleDatePickerSubmit: (range: DateRange) => void;
  endDate?: string;
  startDate?: string;
  label: string;
}

export function SearchDatePicker({
  selectedRange,
  handleDatePickerSubmit,
  label,
  startDate = format(new Date(), "M/d/y"),
  endDate = format(new Date(), "M/d/y"),
}: SearchDatePickerProps) {
  const { isDesktop } = useBreakpoint();

  const [isFacetOpen, setIsFacetOpen] = useState(false);

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  useEffect(() => {
    if (isDesktop) {
      setIsFacetOpen(true);
    }
    return () => setIsFacetOpen(false);
  }, [isDesktop]);

  const formatDate = (date: Date | undefined): string =>
    date && isValid(date) ? format(date, "M/d/y") : "";

  useEffect(() => {
    setFromDate(formatDate(selectedRange.from));
    setToDate(formatDate(selectedRange.to));
  }, [selectedRange]);

  const handleRangeSelect: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    if (range) {
      const { from, to } = range;
      setFromDate(formatDate(from));
      setToDate(formatDate(to));
    }
  };

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "from") {
      setFromDate(value);
    } else if (name === "to") {
      setToDate(value);
    }
  };

  const validateAndSubmit = () => {
    const from = stringToDate(fromDate);
    const to = stringToDate(toDate);

    handleDatePickerSubmit({ from, to });
  };

  const onFieldKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      validateAndSubmit();
    }
  };

  const formatWeekdayName: DateFormatter = (date, options) => {
    return format(date, "EEEEE", options);
  };

  return (
    <Collapsible.Root
      className="hbs-facets"
      open={isFacetOpen}
      onOpenChange={setIsFacetOpen}
    >
      <fieldset>
        <Collapsible.Trigger
          className="hbs-facets__accordion-header-button"
          data-testid="search-date-toggle"
        >
          <legend className="hbs-facets__title">
            {label ? label : "Dates"}
          </legend>
          <Icon name="caretDown" className="hbs-facets__caret" />
        </Collapsible.Trigger>

        <Collapsible.CollapsibleContent className="hbs-facets__accordion-menu">
          <Popover.Root
            onOpenChange={(open) => {
              if (!open) {
                validateAndSubmit();
              }
            }}
          >
            <div className="hbs-facets__date-picker-wrapper">
              <Popover.Anchor asChild>
                <div className="hbs-facets__date-picker-inputs">
                  <input
                    type="text"
                    className="hbs-facets__date-picker-input-field"
                    placeholder={startDate}
                    value={fromDate}
                    aria-label="Start date"
                    name="from"
                    onChange={onFieldChange}
                    onKeyDown={onFieldKeyDown}
                  />

                  <span
                    className="hbs-facets__date-picker-input-separator"
                    aria-hidden
                  >
                    &ndash;
                  </span>

                  <input
                    type="text"
                    className="hbs-facets__date-picker-input-field"
                    placeholder={endDate}
                    value={toDate}
                    aria-label="End date"
                    name="to"
                    onChange={onFieldChange}
                    onKeyDown={onFieldKeyDown}
                  />
                  <Popover.Trigger
                    aria-label="Open calendar"
                    className="hbs-facets__date-picker-calendar-button"
                    data-testid="date-picker-button"
                  >
                    <Icon
                      name="calendar"
                      className="hbs-facets__date-picker-calendar-icon"
                    />
                  </Popover.Trigger>
                </div>
              </Popover.Anchor>
            </div>
            <Popover.Portal>
              <Popover.Content sideOffset={5} side="bottom" align="start">
                <div>
                  <DayPicker
                    mode="range"
                    weekStartsOn={1}
                    showOutsideDays={true}
                    formatters={{ formatWeekdayName }}
                    selected={{
                      from: stringToDate(fromDate),
                      to: stringToDate(toDate),
                    }}
                    onSelect={handleRangeSelect}
                    defaultMonth={stringToDate(fromDate) || new Date()}
                  />
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </Collapsible.CollapsibleContent>
      </fieldset>
    </Collapsible.Root>
  );
}
